import { Button } from '@odekoteam/doppio';
import { ReactNode } from 'react';
import { Box, Checkbox, Flex, Grid, Heading, Label, Paragraph } from 'theme-ui';

import {
  HiddenMarket,
  hiddenMarketEntries,
  unhideAllIndexes,
} from '@/components/Collection/categoryBannerMarkets';
import { sectionStyle } from '@/components/Collection/MarketExceptionSection';

/**
 * The markets this banner is hidden in, as a checkbox grid. There is no
 * "hide all": hiding is a deliberate per-market choice, while "Unhide all"
 * is the safe bulk undo.
 */
export default function HiddenMarketsSection({
  description,
  markets,
  onToggle,
  disabled,
}: {
  description?: ReactNode;
  markets: HiddenMarket[];
  onToggle: (index: number, next: boolean) => void;
  disabled?: boolean;
}) {
  const entries = hiddenMarketEntries(markets);
  const hiddenIndexes = unhideAllIndexes(markets);

  return (
    <Box sx={sectionStyle}>
      <Flex
        sx={{ gap: 3, alignItems: 'baseline', justifyContent: 'space-between' }}
      >
        <Heading variant="headingSm">
          Hidden in ({hiddenIndexes.length})
        </Heading>
        {hiddenIndexes.length > 0 && (
          <Button
            variant="ghost"
            isDisabled={disabled}
            onPress={() =>
              hiddenIndexes.forEach((index) => onToggle(index, false))
            }
          >
            Unhide all
          </Button>
        )}
      </Flex>
      {description && (
        <Paragraph variant="paragraphSm" sx={{ color: 'neutralDark' }}>
          {description}
        </Paragraph>
      )}
      <Grid
        sx={{
          mt: 3,
          gap: 2,
          gridTemplateColumns: ['1fr', 'repeat(2, 1fr)', 'repeat(3, 1fr)'],
        }}
      >
        {entries.map((entry) => (
          <Label
            key={entry.index}
            sx={{
              display: 'flex',
              overflow: 'hidden',
              position: 'relative',
              alignItems: 'center',
              flexDirection: 'row',
              opacity: entry.disabled ? 0.5 : 1,
              cursor: disabled || entry.disabled ? 'default' : 'pointer',
            }}
          >
            <Checkbox
              checked={entry.checked}
              disabled={disabled || entry.disabled}
              onChange={() => onToggle(entry.index, !entry.checked)}
            />
            {entry.label}
          </Label>
        ))}
      </Grid>
    </Box>
  );
}
